import { useEffect, useMemo, useState } from 'react';
import {
  getPickupPreference,
  upsertPickupPreference,
} from '../api/pickup-preferences';
import { PickupPreference } from '../types';
import {
  StoredPickupSelection,
  getStoredPickupSelectionForUserKeys,
  normalizePickupLabel,
  saveStoredPickupSelectionForUserKeys,
} from '../utils/storePickup';

interface UsePickupPreferenceOptions {
  enabled?: boolean;
  storeId: string;
  userId?: string;
}

const toStoredPickupSelection = (
  preference: PickupPreference,
): StoredPickupSelection => ({
  dateValue: preference.dateValue,
  hour: preference.hour,
  label: normalizePickupLabel(preference.label),
  updatedAt: preference.updatedAt,
});

export const usePickupPreference = ({
  enabled = true,
  storeId,
  userId,
}: UsePickupPreferenceOptions) => {
  const [pickupSelection, setPickupSelection] =
    useState<StoredPickupSelection | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const hasBuyerToken =
    typeof window !== 'undefined' &&
    !!window.localStorage.getItem('buyer_token');

  const cacheUserKeys = useMemo(() => {
    const keys = [userId, 'buyer'];
    return keys.filter(
      (key, index, array): key is string =>
        !!key && array.indexOf(key) === index,
    );
  }, [userId]);

  useEffect(() => {
    if (!enabled || !storeId) {
      setPickupSelection(null);
      setIsLoading(false);
      return;
    }

    let cancelled = false;

    const localSelection = getStoredPickupSelectionForUserKeys(
      storeId,
      cacheUserKeys,
    );

    if (!hasBuyerToken) {
      setPickupSelection(localSelection);
      setIsLoading(false);
      return;
    }

    const loadPickupPreference = async () => {
      setIsLoading(true);

      try {
        const remoteSelection = await getPickupPreference(storeId);
        if (cancelled) return;

        if (remoteSelection) {
          const nextSelection = toStoredPickupSelection(remoteSelection);
          saveStoredPickupSelectionForUserKeys(
            storeId,
            nextSelection,
            cacheUserKeys,
          );
          setPickupSelection(nextSelection);
          return;
        }

        if (localSelection) {
          try {
            const migratedSelection = await upsertPickupPreference(
              storeId,
              localSelection,
            );
            if (cancelled) return;

            const nextSelection = toStoredPickupSelection(migratedSelection);
            saveStoredPickupSelectionForUserKeys(
              storeId,
              nextSelection,
              cacheUserKeys,
            );
            setPickupSelection(nextSelection);
            return;
          } catch (migrationError) {
            console.error(
              'Failed to migrate local pickup selection to server',
              migrationError,
            );
          }
        }

        setPickupSelection(localSelection);
      } catch (error) {
        console.error('Failed to fetch pickup preference', error);
        setPickupSelection(localSelection);
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    loadPickupPreference();

    return () => {
      cancelled = true;
    };
  }, [cacheUserKeys, enabled, hasBuyerToken, storeId]);

  const savePickupSelection = async (selection: StoredPickupSelection) => {
    saveStoredPickupSelectionForUserKeys(storeId, selection, cacheUserKeys);
    setPickupSelection(selection);

    if (!hasBuyerToken) {
      return selection;
    }

    const remoteSelection = await upsertPickupPreference(storeId, selection);
    const nextSelection = toStoredPickupSelection(remoteSelection);

    saveStoredPickupSelectionForUserKeys(storeId, nextSelection, cacheUserKeys);
    setPickupSelection(nextSelection);

    return nextSelection;
  };

  return {
    pickupSelection,
    isLoading,
    savePickupSelection,
  };
};
